import React, { useState, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Factory, LogOut, ChevronDown, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { NAV, getRouteMeta } from "@/config/nav";

const STORAGE_KEY = "nisargah_nav_expanded";

export default function Sidebar() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = (r) => r === "*" || (Array.isArray(r) && r.includes(user?.role));

  const activeGroup = getRouteMeta(location.pathname)?.group || null;

  const [expanded, setExpanded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []); } catch (e) { return new Set(); }
  });

  const openGroups = useMemo(() => {
    const s = new Set(expanded);
    if (activeGroup) s.add(activeGroup);
    return s;
  }, [expanded, activeGroup]);

  const toggle = (label) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      s.has(label) ? s.delete(label) : s.add(label);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...s])); } catch (e) {}
      return s;
    });
  };

  const topLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
      isActive ? "bg-sky-600/20 text-sky-200 border-l-2 border-sky-400" : "hover:bg-slate-800/70 hover:text-white text-slate-400"
    }`;

  const childLeaf = (it) => {
    if (it.type === "soon") {
      return (
        <div key={it.testid} data-testid={it.testid} title="Coming soon"
          className="flex items-center gap-3 pl-9 pr-3 py-2 rounded-md text-sm text-slate-500/70 cursor-not-allowed select-none">
          <it.icon className="h-4 w-4 opacity-60" />
          <span className="flex-1 truncate">{it.label}</span>
          <span className="text-[9px] uppercase tracking-wider bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">Soon</span>
        </div>
      );
    }
    if (it.type === "shortcut") {
      return (
        <button key={it.testid} data-testid={it.testid} onClick={() => navigate(it.to)}
          className="w-full flex items-center gap-3 pl-9 pr-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800/70 hover:text-white transition-colors">
          <it.icon className="h-4 w-4" />
          <span className="flex-1 truncate text-left">{it.label}</span>
          <ArrowUpRight className="h-3 w-3 opacity-50" />
        </button>
      );
    }
    return (
      <NavLink key={it.testid} to={it.to} end={it.to === "/"} data-testid={it.testid}
        className={({ isActive }) =>
          `flex items-center gap-3 pl-9 pr-3 py-2 rounded-md text-sm transition-colors ${
            isActive ? "bg-sky-600/20 text-sky-200" : "hover:bg-slate-800/70 hover:text-white text-slate-400"
          }`}>
        <it.icon className="h-4 w-4" />
        <span className="truncate">{it.label}</span>
      </NavLink>
    );
  };

  return (
    <aside
      className="hidden md:flex md:flex-col w-64 shrink-0 bg-slate-900 text-slate-300 min-h-screen sticky top-0"
      data-testid="app-sidebar"
    >
      <div className="px-6 py-5 border-b border-slate-800">
        <div className="flex items-center gap-2">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt={branding.name} data-testid="sidebar-logo"
              className="h-8 w-8 rounded-md object-contain bg-white/10" />
          ) : (
            <div className="h-8 w-8 rounded-md bg-sky-500/20 border border-sky-500/40 flex items-center justify-center">
              <Factory className="h-4 w-4 text-sky-300" />
            </div>
          )}
          <div>
            <div className="font-bold text-white tracking-tight truncate max-w-[150px]" style={{ fontFamily: "Chivo" }} data-testid="sidebar-company-name">{branding.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">O&M Digital</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-thin">
        {NAV.map((node) => {
          if (node.type === "link") {
            if (!allowed(node.roles)) return null;
            return (
              <NavLink key={node.testid} to={node.to} end={node.to === "/"} data-testid={node.testid} className={topLinkClass}>
                <node.icon className="h-4 w-4" />
                <span>{node.label}</span>
              </NavLink>
            );
          }
          // group
          const kids = node.children.filter((c) => allowed(c.roles));
          if (!kids.length) return null;
          const open = openGroups.has(node.label);
          const isActiveGroup = node.label === activeGroup;
          return (
            <div key={node.label}>
              <button
                onClick={() => toggle(node.label)}
                data-testid={`${node.testid}-toggle`}
                aria-expanded={open}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActiveGroup ? "text-white" : "text-slate-300 hover:bg-slate-800/70 hover:text-white"
                }`}
              >
                <node.icon className="h-4 w-4" />
                <span className="flex-1 text-left font-medium">{node.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
              </button>
              {open && (
                <div className="mt-0.5 space-y-0.5" data-testid={`${node.testid}-children`}>
                  {kids.map(childLeaf)}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-3 border-t border-slate-800">
        <div className="px-3 py-2 text-xs text-slate-400">
          <div className="text-slate-200 font-medium truncate">{user?.name}</div>
          <div className="truncate">{user?.email}</div>
          <div className="mt-1 inline-block bg-slate-800 text-sky-300 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider">
            {user?.role?.replace("_", " ")}
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="sidebar-logout-button"
          className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm rounded-md hover:bg-slate-800 text-slate-300 hover:text-white transition"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </aside>
  );
}
